import React, { useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    FormHelperText,
    IconButton,
    useTheme,
} from '@mui/material';
import { Controller, useWatch } from 'react-hook-form';
import { DragIndicator } from '@mui/icons-material';
import { ConfirmDialog } from '@/components/common';
import QuestionActionButtons from '../../campaigns/CampaignQuestionsEditor/edit/QuestionFields/QuestionActionButtons';
import type { Control } from 'react-hook-form';
import type { FormValues } from '../hooks/validationSchema';
import type { GlobalPromptQuestionType } from '../types';
import { getQuestionEditPaperStyles } from '@/components/campaigns/CampaignQuestionsEditor/utils';

export const ANSWER_TYPE_OPTIONS: { value: GlobalPromptQuestionType; label: string }[] = [
    { value: 'text', label: 'Freeform text' },
    { value: 'boolean', label: 'Yes / No' },
    { value: 'numeric', label: 'Numeric scale (1–10)' },
];

export const MAX_TITLE_LENGTH = 120;

interface GlobalPromptQuestionEditCardProps {
    fieldKey: string;
    index: number;
    control: Control<FormValues>;
    displayNumber: number;
    isFirst: boolean;
    isLast: boolean;
    isHighlighted: boolean;
    dragHandleProps?: Record<string, unknown>;
    onDelete: () => void;
    onMoveUp?: () => void;
    onMoveDown?: () => void;
}

const GlobalPromptQuestionEditCard: React.FC<GlobalPromptQuestionEditCardProps> = ({
    fieldKey,
    index,
    control,
    displayNumber,
    isFirst,
    isLast,
    isHighlighted,
    dragHandleProps,
    onDelete,
    onMoveUp,
    onMoveDown,
}) => {
    const [confirmOpen, setConfirmOpen] = useState(false);
    const theme = useTheme();

    const currentTitle = useWatch({ control, name: `questions.${index}.questionTitle` });
    const currentText = useWatch({ control, name: `questions.${index}.questionText` });
    const paperStyles = getQuestionEditPaperStyles(theme, isHighlighted);

    // Mirror the campaign questions behaviour: only confirm when the question has content.
    const handleDeleteClick = () => {
        if (currentTitle?.trim() || currentText?.trim()) {
            setConfirmOpen(true);
            return;
        }

        onDelete();
    };

    const handleConfirmDelete = () => {
        setConfirmOpen(false);
        onDelete();
    };

    return (
        <Paper variant="outlined" sx={paperStyles} data-testid={`global-prompt-question-edit-${displayNumber}`}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {dragHandleProps && (
                        <IconButton
                            {...dragHandleProps}
                            size="small"
                            sx={{ cursor: 'grab', '&:active': { cursor: 'grabbing' }, padding: 0.25, marginLeft: -0.5 }}
                            aria-label="Drag to reorder"
                        >
                            <DragIndicator fontSize="small" />
                        </IconButton>
                    )}
                    <Typography variant="h5">{displayNumber}. Question</Typography>
                </Box>
                <QuestionActionButtons
                    onDelete={handleDeleteClick}
                    onMoveUp={onMoveUp}
                    onMoveDown={onMoveDown}
                    isFirst={isFirst}
                    isLast={isLast}
                />
            </Box>

            <Box sx={{ mb: 1 }}>
                <Controller
                    name={`questions.${index}.questionTitle`}
                    control={control}
                    render={({ field }) => (
                        <>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <InputLabel htmlFor={`gp-title-${fieldKey}`}>Title (optional)</InputLabel>
                                <Box
                                    component="span"
                                    sx={{
                                        fontSize: '0.75rem',
                                        color: field.value.length >= MAX_TITLE_LENGTH ? 'error.main' : 'text.secondary',
                                    }}
                                >
                                    {field.value.length}/{MAX_TITLE_LENGTH}
                                </Box>
                            </Box>
                            <TextField
                                {...field}
                                id={`gp-title-${fieldKey}`}
                                fullWidth
                                size="small"
                                variant="outlined"
                                onChange={(e) => field.onChange(e.target.value.slice(0, MAX_TITLE_LENGTH))}
                            />
                        </>
                    )}
                />
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ flexGrow: 7, flexBasis: { xs: '100%', lg: '60%' } }}>
                    <Controller
                        name={`questions.${index}.questionText`}
                        control={control}
                        render={({ field, fieldState }) => (
                            <>
                                <InputLabel htmlFor={`gp-text-${fieldKey}`} error={!!fieldState.error}>
                                    Question
                                </InputLabel>
                                <TextField
                                    {...field}
                                    id={`gp-text-${fieldKey}`}
                                    fullWidth
                                    size="small"
                                    variant="outlined"
                                    multiline
                                    minRows={3}
                                    maxRows={10}
                                    error={!!fieldState.error}
                                />
                                {fieldState.error && <FormHelperText error>{fieldState.error.message}</FormHelperText>}
                            </>
                        )}
                    />
                </Box>
                <Box sx={{ flexGrow: 4, flexBasis: { xs: '100%', lg: '6rem' } }}>
                    <Controller
                        name={`questions.${index}.questionType`}
                        control={control}
                        render={({ field }) => (
                            <FormControl size="small" fullWidth>
                                <InputLabel id={`gp-type-label-${fieldKey}`}>Answer type</InputLabel>
                                <Select {...field} labelId={`gp-type-label-${fieldKey}`} id={`gp-type-${fieldKey}`} label="Answer type">
                                    {ANSWER_TYPE_OPTIONS.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    />
                </Box>
            </Box>
            <ConfirmDialog
                open={confirmOpen}
                title="Delete question"
                message="Are you sure you want to delete this question? This action cannot be undone."
                onConfirm={handleConfirmDelete}
                onCancel={() => setConfirmOpen(false)}
            />
        </Paper>
    );
};

export default GlobalPromptQuestionEditCard;
